import React from 'react';
import { ArrowDownIcon, ArrowUpIcon, EyeIcon, EyeOffIcon } from 'lucide-react';
import { Modal } from '../ui/Modal';
import { Button } from '../ui/Button';
import { Badge } from '../ui/Badge';
import type { WidgetDefinition } from './widgets/registry';
import type { WidgetLayoutController } from '../../hooks/useWidgetLayout';
interface Props {
  open: boolean;
  onClose: () => void;
  widgets: WidgetDefinition[];
  layout: WidgetLayoutController;
}

/**
 * Arranging the dashboard. Every change applies immediately behind the dialog —
 * there is nothing to publish, because this is one person's view of their own
 * console, not a platform setting. The dialog says so plainly so it is not
 * mistaken for a configuration change others will inherit.
 *
 * Hiding a panel hides a panel. It does not change what the administrator is
 * permitted to see: a widget they have no permission for never reaches this
 * list, and one they hide here is still refused or allowed by the services
 * exactly as before.
 */
export function WidgetLayoutDialog({
  open,
  onClose,
  widgets,
  layout
}: Props) {
  const byId = new Map(widgets.map(widget => [widget.id, widget]));
  return <Modal open={open} onClose={onClose} title="Arrange your dashboard" description="Choose which panels you see and the order they appear in. This affects your view in this browser only — no one else's console changes, and nothing is published." footer={<>
          <Button variant="ghost" onClick={layout.reset} disabled={!layout.customised} data-id="element-480" data-mp="21jd04b4bclwq">
            Restore defaults
          </Button>
          <Button onClick={onClose} data-id="element-481" data-mp="2bews4mfrb0g7">Done</Button>
        </>} data-id="element-479" data-mp="1nn94qehpe7io">
      <ul className="divide-y divide-line" data-id="element-482" data-mp="2ge9g5aid6dkd">
        {layout.ordered.map((id, index) => {
        const widget = byId.get(id);
        if (!widget) return null;
        const hidden = layout.isHidden(id);
        return <li key={id} className="flex flex-wrap items-center gap-3 py-3" data-id="element-483" data-mp="m19r6qs3g70f" data-mpi={index}>
              <div className="min-w-0 flex-1" data-id="element-484" data-mp="dzamkutui5gd">
                <p className="truncate text-body font-medium text-ink" data-id="element-485" data-mp="e02cpq4obaz9">{widget.title}</p>
                <p className="text-caption text-ink-subtle" data-id="element-486" data-mp="91r2vfedp0h1">
                  {widget.span === 2 ? 'Wide panel' : 'Standard panel'}
                </p>
              </div>
              {hidden && <Badge tone="neutral" data-id="element-487" data-mp="ixls4td3xtss">Hidden</Badge>}
              <div className="flex items-center gap-1.5" data-id="element-488" data-mp="j0zuw2poo50r">
                <Button variant="ghost" size="sm" onClick={() => layout.move(id, -1)} disabled={index === 0} aria-label={`Move ${widget.title} up`} icon={<ArrowUpIcon className="h-4 w-4" data-id="element-490" data-mp="11p4uqz37z5ua" />} data-id="element-489" data-mp="wx3l7kxfrt7y" />
                <Button variant="ghost" size="sm" onClick={() => layout.move(id, 1)} disabled={index === layout.ordered.length - 1} aria-label={`Move ${widget.title} down`} icon={<ArrowDownIcon className="h-4 w-4" data-id="element-492" data-mp="1uco31kofllzv" />} data-id="element-491" data-mp="11pwqqb0qb5l3" />
                <Button variant="secondary" size="sm" onClick={() => layout.toggle(id)} icon={hidden ? <EyeIcon className="h-4 w-4" data-id="element-494" data-mp="1ld9behwj35ds" /> : <EyeOffIcon className="h-4 w-4" data-id="element-495" data-mp="1q9894w3qxzqv" />} data-id="element-493" data-mp="1zaftsbdcb22h">
                  {hidden ? 'Show' : 'Hide'}
                </Button>
              </div>
            </li>;
      })}
      </ul>
    </Modal>;
}